<template>
  <nav class="main-nav">
    <!-- Desktop Navigation -->
    <div class="main-nav-desktop">
      <div class="main-nav-content">
        <!-- Home/Dashboard Link -->
        <router-link to="/" class="nav-item home-link" :class="{ active: isHomePage }">
          <svg class="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            <polyline points="9 22 9 12 15 12 15 22"></polyline>
          </svg>
          <span class="nav-text">Home</span>
        </router-link>
        
        <!-- Tool Groups Navigation -->
        <div class="nav-group tools-nav">
          <!-- Tool Groups with Dropdowns (nur Gruppen mit zugänglichen Tools) -->
          <div 
            v-for="group in toolsStore.activeToolGroups" 
            :key="group.id"
            class="nav-group-dropdown"
          >
            <div class="nav-dropdown">
              <button 
                class="nav-item nav-dropdown-toggle"
              >
                <span class="nav-text">{{ group.name }}</span>
              </button>
              
              <!-- Dropdown Menu -->
              <div class="nav-dropdown-menu">
                <router-link 
                  v-for="tool in group.tools" 
                  :key="tool.id"
                  :to="tool.route"
                  class="nav-dropdown-item"
                  :class="{ 'active': isActiveRoute(tool.route) }"
                >
                  <span class="nav-icon" v-html="tool.icon"></span>
                  <span class="nav-text">{{ tool.name }}</span>
                </router-link>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Admin Navigation-->
          <router-link to="/admin" v-if="isAdmin" class="admin-nav nav-item" :class="{ active: isAdminRoute }">
            <svg class="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <span class="nav-text">Administration</span>
          </router-link>
      </div>
    </div>
    
    <!-- Mobile Navigation Toggle -->
    <div class="mobile-nav-toggle">
      <button 
        class="hamburger-menu" 
        @click="toggleMobileNav"
        :aria-expanded="isMobileNavOpen"
        aria-controls="mobile-nav"
        aria-label="Navigation Menu"
      >
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
        <span class="hamburger-line"></span>
      </button>
    </div>
    
    <!-- Mobile Navigation Dropdown -->
    <transition name="mobile-nav-transition">
      <div v-if="isMobileNavOpen" id="mobile-nav" class="main-nav-mobile">
        <div class="mobile-nav-content">
          <!-- Home/Dashboard Link -->
          <router-link to="/" class="nav-item home-link" :class="{ active: isHomePage }" @click="closeMobileNav">
            <svg class="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
            <span class="nav-text">Home</span>
          </router-link>
          
          <!-- Tool Groups Navigation -->
          <div class="mobile-nav-group" v-if="toolsStore.activeToolGroups.length > 0">
            <h3 class="mobile-nav-title">Tools</h3>
            <nav class="nav-links">
              <!-- Tool Groups with Dropdowns -->
              <div 
                v-for="group in toolsStore.activeToolGroups" 
                :key="group.id"
                class="nav-group"
              >
                <button 
                  class="nav-group-toggle"
                  :class="{ 'expanded': expandedGroups.includes(group.id) }"
                  @click="toggleGroup(group.id)"
                >
                  <span class="nav-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"/>
                      <path d="M8 1v6m8-6v6"/>
                    </svg>
                  </span>
                  <span class="nav-text">{{ group.name }}</span>
                  <span class="nav-arrow">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                    </svg>
                  </span>
                </button>
                
                <!-- Group Tools Dropdown -->
                <div 
                  class="nav-group-tools"
                  :class="{ 'expanded': expandedGroups.includes(group.id) }"
                >
                  <router-link 
                    v-for="tool in group.tools" 
                    :key="tool.id"
                    :to="tool.route"
                    class="nav-link nav-tool-link"
                    :class="{ 'active': isActiveRoute(tool.route) }"
                    @click="closeMobileNav"
                  >
                    <span class="nav-icon" v-html="tool.icon"></span>
                    <span class="nav-text">{{ tool.name }}</span>
                  </router-link>
                </div>
              </div>
              
            </nav>
          </div>
          
          <!-- Admin Navigation -->
          <div v-if="isAdmin" class="mobile-nav-group">
            <h3 class="mobile-nav-title">Administration</h3>
            <router-link to="/admin" class="nav-item" :class="{ active: isAdminRoute }" @click="closeMobileNav">
              <svg class="nav-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <span class="nav-text">Administration</span>
            </router-link>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useToolsStore } from '../stores/tools';

// Stores initialisieren
const authStore = useAuthStore();
const toolsStore = useToolsStore();
const route = useRoute();

// Benutzerberechtigungen
const isAdmin = computed(() => authStore.isAdmin);

// Tools
const activeTools = computed(() => {
  console.log('[MainNav] Debug - toolsStore.activeTools:', toolsStore.activeTools);
  console.log('[MainNav] Debug - toolsStore.tools:', toolsStore.tools);
  console.log('[MainNav] Debug - toolsStore.userPermissions:', toolsStore.userPermissions);
  console.log('[MainNav] Debug - authStore.isAuthenticated:', authStore.isAuthenticated);
  return toolsStore.activeTools;
});

// Mobile Navigation Status
const isMobileNavOpen = ref(false);

// Tool Groups State
const expandedGroups = ref<number[]>([]);

// Aktuelle Route überprüfen
const isHomePage = computed(() => route.path === '/');
const isAdminRoute = computed(() => route.path.startsWith('/admin'));

// Entfernt - keine ungroupierten Tools mehr anzeigen

// Prüfen, ob ein bestimmtes Tool aktiv ist
function isActiveTool(toolId: number | string) {
  return route.path.includes(`/tools/${toolId}`);
}

// Prüfen, ob eine Route aktiv ist
function isActiveRoute(routePath: string) {
  return route.path === routePath || route.path.startsWith(routePath);
}

// Tool-Gruppe erweitern/zusammenklappen
function toggleGroup(groupId: number) {
  const index = expandedGroups.value.indexOf(groupId);
  if (index > -1) {
    expandedGroups.value.splice(index, 1);
  } else {
    expandedGroups.value.push(groupId);
  }
}

// Dropdown schließen
function closeDropdown(groupId: number) {
  const index = expandedGroups.value.indexOf(groupId);
  if (index > -1) {
    expandedGroups.value.splice(index, 1);
  }
}

// Tool-Icon rendern (mit Fallback)
function getToolIcon(tool: any) {
  // Wenn das Icon eine SVG-Zeichenkette ist
  if (tool.icon && tool.icon.startsWith('<svg')) {
    return tool.icon;
  }
  
  // Wenn das Icon ein Emoji oder Text ist
  return `<span>${tool.icon}</span>`;
}

// Mobile Navigation umschalten
function toggleMobileNav() {
  isMobileNavOpen.value = !isMobileNavOpen.value;
  
  // UI-Einstellungen speichern
  saveUISettings();
}

// Mobile Navigation schließen
function closeMobileNav() {
  if (isMobileNavOpen.value) {
    isMobileNavOpen.value = false;
    saveUISettings();
  }
}

// Außerhalb der Navigation klicken, um sie zu schließen
function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  const navElement = document.querySelector('.main-nav');
  
  if (navElement && !navElement.contains(target) && isMobileNavOpen.value) {
    closeMobileNav();
  }
}

// UI-Einstellungen laden
function loadUISettings() {
  try {
    // Einstellungen aus localStorage laden
    const uiSettings = JSON.parse(localStorage.getItem('uiSettings') || '{}');
    if (uiSettings.mobileNavOpen !== undefined) {
      isMobileNavOpen.value = uiSettings.mobileNavOpen;
    }
  } catch (error) {
    console.error('Fehler beim Laden der UI-Einstellungen:', error);
  }
}

// UI-Einstellungen speichern
function saveUISettings() {
  try {
    // Einstellungen in localStorage speichern
    const uiSettings = JSON.parse(localStorage.getItem('uiSettings') || '{}');
    uiSettings.mobileNavOpen = isMobileNavOpen.value;
    localStorage.setItem('uiSettings', JSON.stringify(uiSettings));
  } catch (error) {
    console.error('Fehler beim Speichern der UI-Einstellungen:', error);
  }
}

// Event-Listener für Klicks außerhalb der Navigation
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  loadUISettings();
  
  // Tools laden, falls noch nicht geschehen
  if (!activeTools.value.length) {
    toolsStore.loadToolsAndPermissions();
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Hauptnavigation Container */
.main-nav {
  position: sticky;
  top: var(--main-nav-top, 0);
  width: 100%;
  background-color: var(--color-primary-dark);
  color: var(--color-text-on-primary, white);
  z-index: 90;
  /* Synchronisierte Transitions mit Sidebar */
  transition: transform var(--sidebar-transition-duration) ease, 
              top var(--sidebar-transition-duration) ease, 
              background-color var(--sidebar-transition-duration) ease, 
              color var(--sidebar-transition-duration) ease;
}

/* Dark-Mode Anpassungen für MainNav */
:global(.mode-dark) .main-nav {
  background-color: var(--color-background-dark, #1d1d1b);
  color: var(--color-text, #ffffff);
  box-shadow: 0 2px 8px var(--shadow-color, rgba(0, 0, 0, 0.5));
}

/* Desktop Navigation */
.main-nav-desktop {
  display: flex;
  justify-content: center;
  width: 100%;
}

.main-nav-content {
  display: flex;
  align-items: center;
  max-width: 1200px;
  width: 100%;
  height: 48px;
  padding: 0 var(--spacing-md);
}

/* Navigation Items */
.nav-item {
  display: flex;
  align-items: center;
  padding: 0 var(--spacing-md);
  height: 100%;
  color: var(--color-text-on-primary, white);
  text-decoration: none;
  transition: background-color 0.2s, color 0.2s;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.nav-item.active {
  background-color: rgba(255, 255, 255, 0.1);
  border-bottom: 3px solid var(--color-accent);
}

/* Dark-Mode Hover und aktiver Zustand */
:global(.mode-dark) .nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

:global(.mode-dark) .nav-item.active {
  background-color: rgba(255, 255, 255, 0.1) !important;
  border-bottom: 3px solid var(--color-accent);
}

.nav-icon {
  margin-right: var(--spacing-sm);
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-text {
  margin-left: var(--spacing-sm);
  font-weight: 500;
  white-space: nowrap;
  font-size: var(--font-size-sm);
}

/* Desktop Tool Groups Navigation */
.nav-group-dropdown {
  position: relative;
  display: inline-block;
}

.nav-dropdown {
  position: relative;
}

.nav-dropdown-toggle {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0 var(--spacing-md);
  height: 48px;
  transition: background-color 0.2s, color 0.2s;
}

.nav-dropdown:hover .nav-dropdown-toggle {
  background-color: rgba(255, 255, 255, 0.1);
}

.nav-dropdown:hover .nav-dropdown-toggle.active,
.nav-dropdown-toggle.active {
  border-bottom: 3px solid var(--color-accent);
}

.nav-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 200px;
  background-color: var(--color-primary-dark);
  box-shadow: var(--shadow-md, 0 0.5rem 1rem rgba(0, 0, 0, 0.15));
  border-radius: 0;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: opacity 0.2s, visibility 0.2s, transform 0.2s;
  z-index: 100;
}

.nav-dropdown:hover .nav-dropdown-menu {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

:global(.mode-dark) .nav-dropdown-menu {
  background-color: var(--color-background-dark, #1d1d1b);
  border: 1px solid var(--color-border, #57747a);
}

.nav-dropdown-item {
  display: flex;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-md);
  color: var(--color-text-on-primary, white);
  text-decoration: none;
  transition: background-color 0.2s;
  border-left: 3px solid transparent;
  white-space: nowrap;
}

.nav-dropdown-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-left-color: var(--color-accent);
}

.nav-dropdown-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  border-left-color: var(--color-accent);
}

:global(.mode-dark) .nav-dropdown-item {
  color: var(--color-text, #ffffff);
}

:global(.mode-dark) .nav-dropdown-item:hover {
  background-color: var(--color-hover, rgba(29, 29, 27, 0.1));
}

/* Mobile Tool Groups Navigation */
.nav-group {
  position: relative;
}

.nav-group-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  transition: background-color 0.2s;
}

.nav-group-toggle:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.nav-group-toggle.expanded {
  background-color: rgba(255, 255, 255, 0.15);
}

.nav-group-toggle.expanded .nav-arrow {
  transform: rotate(90deg);
}

.nav-group-tools {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
  background-color: rgba(0, 0, 0, 0.1);
}

.nav-group-tools.expanded {
  max-height: 500px;
}

.nav-tool-link {
  padding-left: calc(var(--spacing-md) + var(--spacing-lg));
  border-left: 2px solid transparent;
}

.nav-tool-link:hover {
  background-color: rgba(255, 255, 255, 0.05);
  border-left-color: var(--color-accent);
}

.nav-tool-link.active {
  background-color: rgba(255, 255, 255, 0.1);
  border-left-color: var(--color-accent);
}

/* Gruppierung von Navigationselementen */
.nav-group {
  display: flex;
  align-items: center;
  justify-content: center;
}

.tools-nav {
  margin-left: var(--spacing-md);
}

.admin-nav {
  margin-left: auto;
}

button {
  border-radius: 0;
}

/* Mobile Navigation Toggle */
.mobile-nav-toggle {
  display: none;
}

.hamburger-menu {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.hamburger-line {
  width: 100%;
  height: 2px;
  background-color: white;
  transition: transform 0.3s, opacity 0.3s, background-color 0.3s;
}

:global(.mode-dark) .hamburger-line {
  background-color: var(--color-text-light);
}

/* Mobile Navigation Dropdown */
.main-nav-mobile {
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: var(--color-primary-dark);
  box-shadow: var(--shadow-md, 0 0.5rem 1rem rgba(0, 0, 0, 0.15));
  z-index: 95;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}

:global(.mode-dark) .main-nav-mobile {
  background-color: var(--color-background-dark, #1d1d1b);
  box-shadow: 0 4px 12px var(--shadow-color, rgba(0, 0, 0, 0.5));
  border-top: 1px solid var(--color-border, #57747a);
}

.mobile-nav-content {
  padding: var(--spacing-md);
}

.mobile-nav-group {
  margin-top: var(--spacing-md);
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--color-primary-light);
}

.mobile-nav-title {
  font-size: var(--font-size-sm);
  font-weight: 600;
  margin-bottom: var(--spacing-sm);
  color: var(--color-accent-light);
}

/* Mobile Navigation Animation */
.mobile-nav-transition-enter-active,
.mobile-nav-transition-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.mobile-nav-transition-enter-from,
.mobile-nav-transition-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive Anpassungen */
@media (max-width: 768px) {
  .main-nav-desktop {
    display: none;
  }
  
  .mobile-nav-toggle {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: var(--spacing-sm) var(--spacing-md);
  }
  
  .main-nav-mobile {
    display: block;
  }
  
  .nav-item {
    padding: var(--spacing-sm) var(--spacing-md);
    margin-bottom: var(--spacing-sm);
    border-radius: var(--border-radius-sm);
  }
  
  /* Icons in mobiler Navigation ausblenden */
  .main-nav-mobile .nav-icon {
    display: none;
  }
  
  /* Text-Abstand anpassen, da Icons ausgeblendet sind */
  .main-nav-mobile .nav-text {
    margin-left: 0;
  }
  
  /* Flex-Layout bei nav-group in mobile Version entfernen */
  .main-nav-mobile .nav-group {
    display: block;
  }
  
  /* Tool-Links in weißer Farbe */
  .main-nav-mobile .nav-tool-link {
    color: white !important;
  }
  
  /* Tool-Links Hover-Status */
  .main-nav-mobile .nav-tool-link:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  /* Aktive Tool-Links auch in weiß */
  .main-nav-mobile .nav-tool-link.active {
    color: white !important;
    background-color: rgba(255, 255, 255, 0.15);
  }
}
</style>
